import { Link } from "react-router-dom";
import { Phone, Mail, Instagram, MapPin, MessageCircle } from "lucide-react";
import { BRAND, IMAGES } from "@/lib/constants";

export default function Contact() {
  const whatsappUrl = `https://wa.me/${BRAND.whatsappPhone}?text=${encodeURIComponent(
    `Merhaba ${BRAND.shortName}, sizinle iletişim kurmak istiyorum.`
  )}`;

  return (
    <div data-testid="page-contact" className="bg-cream-50">
      {/* Header */}
      <section className="relative overflow-hidden pt-8 md:pt-12 pb-14 md:pb-20">
        <div className="aura-bg opacity-50" />
        <div className="relative page-shell grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-9 min-w-0">
            <div className="eyebrow mb-6">İletişim</div>
            <h1 className="max-w-full md:max-w-none font-display text-[16vw] sm:text-[13vw] md:text-[8vw] lg:text-[7.5rem] font-light leading-[0.95] tracking-normal text-midnight">
              Bir <em className="text-lavender-700">mesaj</em>{" "}
              <em className="text-gold-soft italic">uzaktayız</em>.
            </h1>
            <p className="mt-8 w-full max-w-full md:max-w-xl text-lavender-700 leading-relaxed text-[15px]">
              Sorularını, niyetlerini veya kurumsal iş birliği taleplerini
              aşağıdaki kanallardan iletebilirsin.
            </p>
          </div>
        </div>
      </section>

      {/* Contact grid */}
      <section className="pb-16 md:pb-24" data-testid="contact-grid">
        <div className="page-shell grid grid-cols-12 gap-8 md:gap-10">
          <div className="col-span-12 md:col-span-5 img-zoom rounded-sm aspect-[4/5]">
            <img src={IMAGES.candle} alt="dingin alan" />
          </div>

          <div className="col-span-12 md:col-span-6 md:col-start-7 space-y-8 md:space-y-10">
            {[
              {
                icon: Phone,
                label: "Telefon",
                value: BRAND.displayPhone,
                href: `tel:${BRAND.whatsappPhone}`,
                testid: "contact-phone",
              },
              {
                icon: MessageCircle,
                label: "WhatsApp",
                value: "Anında mesajlaşma",
                href: whatsappUrl,
                testid: "contact-whatsapp",
                external: true,
              },
              {
                icon: Mail,
                label: "E-posta",
                value: BRAND.email,
                href: `mailto:${BRAND.email}`,
                testid: "contact-email",
              },
              {
                icon: Instagram,
                label: "Instagram",
                value: BRAND.instagram,
                href: `https://instagram.com/${BRAND.instagram.replace("@", "")}`,
                testid: "contact-instagram",
                external: true,
              },
              {
                icon: MapPin,
                label: "Konum",
                value: BRAND.city,
                href: null,
                testid: "contact-location",
              },
            ].map(({ icon: Icon, label, value, href, testid, external }) => {
              const Inner = (
                <div className="grid grid-cols-12 gap-4 md:gap-6 items-center py-5 md:py-6 border-b border-cream-300 group">
                  <div className="col-span-2 md:col-span-1">
                    <Icon size={20} className="text-gold" />
                  </div>
                  <div className="col-span-10 md:col-span-4 text-[10px] tracking-[0.2em] md:tracking-[0.32em] uppercase text-lavender-700">
                    {label}
                  </div>
                  <div className="col-span-12 md:col-span-7 min-w-0 break-words font-display text-2xl md:text-3xl text-midnight group-hover:text-gold-deep transition-colors duration-500">
                    {value}
                  </div>
                </div>
              );
              return href ? (
                <a
                  key={label}
                  href={href}
                  data-testid={testid}
                  target={external ? "_blank" : undefined}
                  rel={external ? "noreferrer" : undefined}
                  className="block"
                >
                  {Inner}
                </a>
              ) : (
                <div key={label} data-testid={testid}>
                  {Inner}
                </div>
              );
            })}

            <Link
              to="/randevu"
              data-testid="contact-cta-appointment"
              className="btn-midnight mt-4"
            >
              Randevu Almak İstiyorum →
            </Link>
          </div>
        </div>
      </section>

      {/* Hours strip */}
      <section className="py-14 md:py-20 bg-lavender-50/50" data-testid="contact-hours">
        <div className="page-shell grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="eyebrow mb-6">Çalışma Saatleri</div>
            <h2 className="font-display text-4xl md:text-5xl font-light text-midnight leading-[1.1]">
              Seans saatleri,
              <br />
              <em className="text-gold-soft">sezgisel</em> ritimde.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6 space-y-4">
            {[
              ["Pazartesi — Cuma", "10:00 — 19:00"],
              ["Cumartesi", "11:00 — 17:00"],
              ["Pazar", "Yalnızca özel randevular"],
            ].map(([d, h]) => (
              <div
                key={d}
                className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1 sm:gap-4 border-b border-cream-300 pb-4"
              >
                <span className="font-display text-2xl text-midnight">{d}</span>
                <span className="text-lavender-700 tracking-wide">{h}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
